import { c as _c } from "react/compiler-runtime";
import { Check, Xmark } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { Stack, Text } from 'ui';
import { useCurrentJourney } from '~/stores/journeyStore';
import { richLink } from '~/utils/richTextTags';
import { SaveButton } from '../Common/SaveButton';
import ShareButton from '../Common/ShareButton/src/ShareButton';
import { Disclosure } from '../Disclosure';
import JourneyCarbonFootprint from './JourneyCarbonFootprint';
type JourneyIncludesProps = {
  variant?: 'journey' | 'checkout' | 'journey-mobile';
  details?: {
    tripInDays: number;
  };
  uniqueId?: string;
};
const faqLink = richLink({
  href: '/about/byway-faqs',
  hasUnderline: true,
  target: '_self'
});
const WHATS_INCLUDED = [{
  labelKey: 'includes_transport'
}, {
  labelKey: 'includes_accommodation'
}, {
  labelKey: 'includes_package_protection'
}, {
  labelKey: 'includes_disruption_guarantee'
}, {
  labelKey: 'includes_support'
}, {
  labelKey: 'includes_accessibility'
}] as const;
const WHATS_NOT_INCLUDED = [{
  labelKey: 'excludes_insurance'
}, {
  labelKey: 'excludes_city_taxes'
}, {
  labelKey: 'excludes_seats'
}, {
  labelKey: 'excludes_luggage'
}] as const;
function JourneyIncludesWithJourney(t0) {
  const $ = _c(13);
  const {
    variant,
    details,
    uniqueId
  } = t0;
  const journey = useCurrentJourney();
  const t = useTranslations("journey");
  const tripInDays = journey?.details?.tripInDays || details?.tripInDays;
  const showSaveShareNav = process.env.NEXT_PUBLIC_FF_TRIP_SAVE_SHARE_NAV === "true";
  let t1;
  if ($[0] !== journey?.details?.endDate || $[1] !== journey?.details?.startDate || $[2] !== t || $[3] !== tripInDays || $[4] !== uniqueId || $[5] !== variant) {
    t1 = variant === "journey" && <div className="hidden md:block"><Stack horizontal={true} justify="between" align="center"><Stack horizontal={true} spacing={2} align="center" className="pb-4"><Text as="span" variant="subtitle-2" data-test-id="journey-duration">{t("days_count", {
              count: tripInDays ?? 0
            })}</Text><JourneyCarbonFootprint /></Stack>{!showSaveShareNav && <Stack horizontal={true} spacing={4} className="pb-4"><ShareButton />{uniqueId && <SaveButton tripId={uniqueId} startDate={journey?.details?.startDate} endDate={journey?.details?.endDate} />}</Stack>}</Stack><Stack spacing={3}><Stack horizontal={true} spacing={2}><Text as="span" variant="emphasis-2" className="mr-2 whitespace-nowrap">{t("includes_label")}</Text><ul className="grid grid-cols-3 gap-3 text-xs lg:gap-4 lg:flex">{WHATS_INCLUDED.map(t2 => {
              const {
                labelKey
              } = t2;
              return <li key={`included-${labelKey}`} className="flex items-center gap-1"><div className="flex items-center justify-center flex-shrink-0 w-6 h-6 bg-green-800 rounded-full"><Check decorative={true} color="var(--by-color-icon-contrast)" size="scale" /></div><span>{t(labelKey)}</span></li>;
            })}</ul></Stack></Stack></div>;
    $[0] = journey?.details?.endDate;
    $[1] = journey?.details?.startDate;
    $[2] = t;
    $[3] = tripInDays;
    $[4] = uniqueId;
    $[5] = variant;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t || $[8] !== variant) {
    t2 = variant === "journey-mobile" && <div className="pl-4 pr-2 mt-5 md:hidden"><Stack spacing={3}><Stack spacing={3}><Text as="span" variant="emphasis-2" className="mr-2 whitespace-nowrap">{t("includes_label")}</Text><ul className="flex flex-wrap gap-4 text-xs">{WHATS_INCLUDED.map(t3 => {
              const {
                labelKey: labelKey_0
              } = t3;
              return <li key={`included-${labelKey_0}`} className="flex items-center gap-1"><div className="flex items-center justify-center flex-shrink-0 w-5 h-5 bg-green-800 rounded-full"><Check decorative={true} color="var(--by-color-icon-contrast)" size="scale" /></div><span>{t(labelKey_0)}</span></li>;
            })}</ul></Stack></Stack></div>;
    $[7] = t;
    $[8] = variant;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1 || $[11] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function JourneyIncludesCheckout(t0) {
  const $ = _c(20);
  const {
    uniqueId
  } = t0;
  const t = useTranslations("journey");
  const tAria = useTranslations("aria");
  let t1;
  if ($[0] !== t) {
    t1 = t("includes_title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t || $[3] !== tAria) {
    t2 = WHATS_INCLUDED.map(t3 => {
      const {
        labelKey
      } = t3;
      return <li key={`included-${labelKey}`} className="flex items-center gap-3"><div className="flex items-center justify-center w-5 h-5 text-white bg-green-800 rounded-full"><Check ariaLabel={tAria("included")} size="scale" strokeWidth={2} /></div><span>{t(labelKey)}</span></li>;
    });
    $[2] = t;
    $[3] = tAria;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t || $[6] !== tAria) {
    t3 = WHATS_NOT_INCLUDED.map(t4 => {
      const {
        labelKey: labelKey_0
      } = t4;
      return <li key={`not-included-${labelKey_0}`} className="flex items-center gap-3"><div className="flex items-center justify-center w-5 h-5 text-black border rounded-full shrink-0"><Xmark size="scale" ariaLabel={tAria("not_included")} /></div><span className="line-through">{t(labelKey_0)}</span></li>;
    });
    $[5] = t;
    $[6] = tAria;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <ul className="flex flex-col gap-3 lg:gap-4 lg:flex">{t2}{t3}</ul>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t || $[12] !== uniqueId) {
    t5 = t.rich("includes_luggage_note", {
      faqLink,
      conciergeLink: richLink({
        href: uniqueId ? `/concierge?tripId=${uniqueId}` : "/concierge",
        hasUnderline: true,
        target: "_self"
      })
    });
    $[11] = t;
    $[12] = uniqueId;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t5) {
    t6 = <Text className="pt-4">{t5}</Text>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t1 || $[17] !== t4 || $[18] !== t6) {
    t7 = <div><Disclosure defaultExpanded={true} title={t1} headingLevel="h2" size="lg" headerColour="blueSecondary" showChevron={true} noBorder={true}>{t4}{t6}</Disclosure></div>;
    $[16] = t1;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
export default function JourneyIncludes(t0) {
  const $ = _c(6);
  const {
    variant: t1,
    details,
    uniqueId
  } = t0;
  const variant = t1 === undefined ? "journey" : t1;
  if (variant === "checkout") {
    let t2;
    if ($[0] !== uniqueId) {
      t2 = <JourneyIncludesCheckout uniqueId={uniqueId} />;
      $[0] = uniqueId;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] !== details || $[3] !== uniqueId || $[4] !== variant) {
    t2 = <JourneyIncludesWithJourney variant={variant} details={details} uniqueId={uniqueId} />;
    $[2] = details;
    $[3] = uniqueId;
    $[4] = variant;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}